import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from '../ArticleComponents.module.css';
import JSXStyle from 'styled-jsx/style';

import {
	getGridTemplate,
	getDeviceType,
	processInlineStyles,
} from './Style.js';
import { renderColumnComponents } from './Render';

export default function ColumnsComponent({
	component,
	style,
	styleblock,
	deviceWidth,
	onImageClick,
	onScaleChange,
	lang,
}) {
	const {
		id,
		columns,
		colsplit,
		collapsetype,
		contentmode,
		width,
		backgroundcolor,
		backgroundimage,
		imageurl,
		imagepositionleft,
		imagepositiontop,
		horizontalpadding,
		verticalpadding,
		imageopacity,
	} = component;

	let componentCSS = '';

	const [columnStyle, setColumnStyle] = useState({});
	const [className, setClassName] = useState('component-columns-grid');
	const ref = useRef(null);

	let deviceType;
	let styleBlockObj = {};
	let componentStyle = {};

	// The parse and stringnify is to ensure that we are not keeping the reference
	let incommingStyle = style ? JSON.parse(JSON.stringify(style)) : {};

	if (styleblock) {
		styleBlockObj = styleblock;
		incommingStyle = { ...incommingStyle, ...styleblock.components };
		styleblock = {
			background: `#${styleblock.styleblock['color'].replace('#', '')}`,
			paddingTop:
				`${styleblock.styleblock['styleblock-padding-top']}px` || 0,
			paddingBottom:
				`${styleblock.styleblock['styleblock-padding-bottom']}px` || 0,
			paddingLeft:
				`${styleblock.styleblock['styleblock-padding-horizontal']}px` ||
				0,
			paddingRight:
				`${styleblock.styleblock['styleblock-padding-horizontal']}px` ||
				0,
		};
	} else {
		styleblock = {};
	}

	if (!!component.styles && !!component.styles.length) {
		processInlineStyles(component, incommingStyle);
		for (let s of component.styles) {
			const { canvas } = s.properties;

			if (!canvas) {
				continue;
			}

			const { css } = canvas;
			if (css) {
				componentCSS = `.jsx-${id} {${css}}`;
			}

			const unit = canvas.canvas_unit ? canvas.canvas_unit : 'px';
			styleblock = {
				paddingTop: canvas['canvas-padding-top']
					? `${canvas['canvas-padding-top']}${unit}`
					: 0,
				paddingBottom: canvas['canvas-padding-bottom']
					? `${canvas['canvas-padding-bottom']}${unit}`
					: 0,
				paddingLeft: canvas['canvas-left-padding']
					? `${canvas['canvas-left-padding']}${unit}`
					: 0,
				paddingRight: canvas['canvas-right-padding']
					? `${canvas['canvas-right-padding']}${unit}`
					: 0,
			};

			if (canvas['color']) {
				styleblock['background'] = `#${canvas['color'].replace(
					'#',
					''
				)}`;
			}
		}
	}

	if (backgroundimage && backgroundimage === 'on') {
		let background = [];
		if (backgroundcolor) {
			let color = `#${backgroundcolor.replace('#', '')}`;
			let opacity = imageopacity
				? parseFloat(`${imageopacity}`) / 100
				: 100;
			const { r, g, b } = hexToRgb(color);
			background.push(`linear-gradient(rgba(${r}, ${g}, ${b}, ${opacity}), 
			rgba(${r}, ${g}, ${b}, ${opacity}))`);
		}
		if (imageurl) {
			background.push(`url('${imageurl.replace(/\\/g, '/')}')`);
		}

		if (background.length) {
			componentStyle.background = background.join(', ');
		}

		if (imageurl && imagepositionleft) {
			componentStyle.backgroundPositionX = `${imagepositionleft}%`;
		}
		if (imageurl && imagepositiontop) {
			componentStyle.backgroundPositionY = `${imagepositiontop}%`;
		}
	}

	if(horizontalpadding) {
		componentStyle.paddingLeft = `${horizontalpadding}%`;
		componentStyle.paddingRight = `${horizontalpadding}%`;
	}

	if(verticalpadding) {
		componentStyle.paddingTop = `${verticalpadding}%`;
		componentStyle.paddingBottom = `${verticalpadding}%`;
	}

	useEffect(() => {
		deviceType = getDeviceType({ deviceWidth });
		if (collapsetype === 'responsive' && deviceType === 'mobile') {
			setClassName('component-columns-collapse');
		} else if (collapsetype === 'tablet' && deviceType !== 'desktop') {
			setClassName('component-columns-collapse');
		} else {
			setClassName('component-columns-grid');
		}

		if (className === `component-columns-grid`) {
			setColumnStyle({
				gridTemplateColumns: getGridTemplate({
					colsplit,
					columns,
				}),
				width: `${width}%`,
				gridTemplateAreas: `${columns.map(() => '.').join(' ')}`,
			});
		}
	}, [ref.current, deviceWidth, className]);

	if (contentmode === 'multicol') {
		let multicolStyle = {};
		// It uses the last style to overwrite the property
		if (component?.styles?.length) {
			multicolStyle = component.styles.reduce((acc, stly) => {
				if (stly?.properties?.canvas?.color) {
					acc['background'] = stly?.properties?.canvas?.color;
				}
				return acc;
			}, {});
		}
		return (
			<div
				ref={ref}
				id={id}
				style={multicolStyle}
				className={styles['component-columns-multicol']}
			>
				{renderColumnComponents({
					components: columns[0],
					displayImages: true,
					style: incommingStyle,
					deviceWidth,
					onScaleChange,
					lang,
					onImageClick,
					styleblock: styleBlockObj.id ? styleBlockObj : null,
				})}
			</div>
		);
	}

	return (
		<div
			ref={ref}
			id={id}
			className={`${styles[className]} jsx-${id}`}
			style={{ ...columnStyle, ...styleblock, ...componentStyle }}
		>
			{columns.map((column, i) => {
				return (
					<div key={i}>
						{renderColumnComponents({
							components: column,
							displayImages: true,
							style: incommingStyle,
							onScaleChange,
							lang,
							onImageClick,
							styleblock: styleBlockObj.id ? styleBlockObj : null,
						})}
					</div>
				);
			})}
			<JSXStyle id={id}>{componentCSS}</JSXStyle>
		</div>
	);
}

ColumnsComponent.propTypes = {
	component: PropTypes.object.isRequired,
	style: PropTypes.object,
	styleblock: PropTypes.object,
	deviceWidth: PropTypes.number,
	onImageClick: PropTypes.func,
	onScaleChange: PropTypes.func,
	lang: PropTypes.string,
};

function hexToRgb(hex) {
	var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
				r: parseInt(result[1], 16),
				g: parseInt(result[2], 16),
				b: parseInt(result[3], 16),
		  }
		: null;
}
